<template>
  <div class="settings-design">
    <div class="info-box">
      <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
      Global OTA rates inherited by every property report. Each property can override individually. Where available, actual fees are pulled from your channel manager per booking — the rates below are used as a fallback only.
    </div>

    <div class="card" style="margin-bottom:18px;">
      <div class="card-header">
        <div>
          <div class="card-title"><svg viewBox="0 0 24 24"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>Fee source</div>
          <div class="card-subtitle">Choose how all platform and card fees are calculated on each booking</div>
        </div>
      </div>
      <div class="card-body">
        <div style="display:flex;flex-direction:column;gap:10px;">
          <div
            class="radio-opt"
            :class="{ active: feeSource === 'channel' }"
            @click="feeSource = 'channel'"
            style="display:flex;align-items:flex-start;gap:12px;padding:14px 16px;border-radius:8px;cursor:pointer;"
          >
            <div class="radio-dot-wrap"></div>
            <div style="flex:1;">
              <div style="display:flex;align-items:center;gap:8px;margin-bottom:3px;">
                <span style="font-size:13px;font-weight:600;color:var(--text);">Pull everything from channel manager</span>
                <span style="font-size:11px;font-weight:500;color:var(--green);background:var(--green-dim);border:1px solid var(--green-border);padding:1px 7px;border-radius:20px;">Recommended</span>
              </div>
              <div style="font-size:12px;color:var(--text-muted);line-height:1.5;">OTA commission fees and card processing fees pulled directly from each booking. More accurate — captures Genius tiers, promotions and variable card rates automatically. Rates configured below are used as fallback only when no data is returned.</div>
            </div>
          </div>
          <div
            class="radio-opt"
            :class="{ active: feeSource === 'manual' }"
            @click="feeSource = 'manual'"
            style="display:flex;align-items:flex-start;gap:12px;padding:14px 16px;border-radius:8px;cursor:pointer;"
          >
            <div class="radio-dot-wrap"></div>
            <div>
              <div style="font-size:13px;font-weight:600;color:var(--text);margin-bottom:3px;">Use configured rates only</div>
              <div style="font-size:12px;color:var(--text-muted);line-height:1.5;">Always calculate all fees from the rates configured below. Use this if your channel manager doesn't pass fee data reliably or you prefer full manual control.</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div :style="feeSource === 'channel' ? 'opacity:0.5;pointer-events:none;transition:opacity 0.2s;' : 'transition:opacity 0.2s;'">
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;">
        <span style="font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:0.07em;color:var(--text-muted);">Fallback rates</span>
        <span style="font-size:11px;color:var(--text-muted);font-style:italic;">{{ feeSource === 'channel' ? "Used only when channel manager doesn't return fee data" : 'These rates are applied to all bookings' }}</span>
      </div>

      <div class="card">
        <div class="card-header">
          <div>
            <div class="card-title"><svg viewBox="0 0 24 24"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/></svg>Platform commission rates</div>
            <div class="card-subtitle">{{ feeSource === 'channel' ? 'Fallback rates — used only when channel manager fee data is unavailable' : 'Standard rates — applied to all bookings regardless of channel manager data' }}</div>
          </div>
          <button class="btn btn-outline btn-sm"><svg viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>Add platform</button>
        </div>
        <div class="card-body" style="padding:0;">
          <table class="ota-table">
            <thead><tr>
              <th style="width:32%">Platform</th><th>Base rate</th><th>VAT treatment</th><th>Effective rate</th><th style="text-align:right">Actions</th>
            </tr></thead>
            <tbody>
              <tr>
                <td><div class="ota-name"><div class="ota-logo airbnb">AB</div>Airbnb</div><div class="ota-hint">{{ feeSource === 'channel' ? 'Fallback rate only · host-only fee model · actual fee pulled from channel manager where available' : 'Standard rate · applied to all Airbnb bookings' }}</div></td>
                <td><div style="display:flex;align-items:center;gap:6px;"><input class="ota-rate-input" type="number" v-model.number="rates.airbnb"/><span style="font-size:12px;color:var(--text-muted);">%</span></div></td>
                <td><span class="vat-tag vat-inc">VAT exclusive</span></td>
                <td><span class="ota-effective">{{ rates.airbnb.toFixed(2) }}%</span></td>
                <td style="text-align:right;"><button class="btn btn-outline btn-sm" disabled style="opacity:0.4;cursor:default;">Protected</button></td>
              </tr>
              <tr>
                <td><div class="ota-name"><div class="ota-logo booking">BK</div>Booking.com</div><div class="ota-hint">{{ feeSource === 'channel' ? 'Fallback rate only · set your standard rate here · Genius bookings may be higher and will vary per booking' : 'Standard rate · note: Genius bookings may attract a higher % than configured here' }}</div></td>
                <td><div style="display:flex;align-items:center;gap:6px;"><input class="ota-rate-input" type="number" v-model.number="rates.booking"/><span style="font-size:12px;color:var(--text-muted);">%</span></div></td>
                <td><span class="vat-tag vat-inc">VAT inclusive</span></td>
                <td><span class="ota-effective">{{ rates.booking.toFixed(2) }}%</span></td>
                <td style="text-align:right;"><button class="btn btn-outline btn-sm" disabled style="opacity:0.4;cursor:default;">Protected</button></td>
              </tr>
              <tr>
                <td><div class="ota-name"><div class="ota-logo vrbo">VB</div>VRBO</div><div class="ota-hint">Fallback rate only · 5% commission + 3% processing · actual fee pulled from channel manager where available</div></td>
                <td><div style="display:flex;align-items:center;gap:6px;"><input class="ota-rate-input" type="number" v-model.number="rates.vrbo"/><span style="font-size:12px;color:var(--text-muted);">%</span></div></td>
                <td><span class="vat-tag vat-inc">VAT inclusive</span></td>
                <td><span class="ota-effective">{{ rates.vrbo.toFixed(2) }}%</span></td>
                <td style="text-align:right;"><button class="btn btn-outline btn-sm">Remove</button></td>
              </tr>
              <tr>
                <td><div class="ota-name"><div class="ota-logo custom">DR</div>Direct</div><div class="ota-hint">Applied to direct bookings only · no platform fee · adjust to match your agreements</div></td>
                <td><div style="display:flex;align-items:center;gap:6px;"><input class="ota-rate-input" type="number" v-model.number="rates.direct"/><span style="font-size:12px;color:var(--text-muted);">%</span></div></td>
                <td><span class="vat-tag vat-inc">VAT inclusive</span></td>
                <td><span class="ota-effective">{{ rates.direct.toFixed(2) }}%</span></td>
                <td style="text-align:right;"><button class="btn btn-outline btn-sm">Remove</button></td>
              </tr>
              <tr style="background:#faf9ff;">
                <td colspan="5" style="padding:10px 12px;">
                  <div style="display:flex;align-items:center;justify-content:space-between;">
                    <span style="font-size:12px;color:var(--text-muted);font-style:italic;">All other platforms — catch-all fallback</span>
                    <div style="display:flex;align-items:center;gap:8px;"><input class="ota-rate-input" type="number" v-model.number="rates.other" style="width:64px;"/><span style="font-size:12px;color:var(--text-muted);">%</span><span class="vat-tag vat-unknown">Check with OTA</span><span class="ota-effective">{{ rates.other.toFixed(2) }}%</span></div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="card">
        <div class="card-header">
          <div>
            <div class="card-title"><svg viewBox="0 0 24 24"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>Payment methods &amp; card fees</div>
            <div class="card-subtitle">Configure how payments are taken per booking source and whether a card fee applies</div>
          </div>
        </div>
        <div class="card-body">
          <div style="font-size:12px;color:var(--text-muted);margin-bottom:16px;line-height:1.6;">Set the payment method for each booking source. This determines whether a card processing fee is applied and how it is calculated on each owner report.</div>

          <!-- Airbnb -->
          <div class="payment-platform-row">
            <div class="payment-platform-header" @click="expanded.airbnb = !expanded.airbnb">
              <div style="display:flex;align-items:center;gap:10px;">
                <div class="ota-logo airbnb" style="width:30px;height:30px;border-radius:7px;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:600;">AB</div>
                <div>
                  <div style="font-size:13px;font-weight:600;color:var(--text);">Airbnb</div>
                  <div style="font-size:11px;color:var(--text-muted);margin-top:1px;">Payments handled entirely by Airbnb</div>
                </div>
              </div>
              <div style="display:flex;align-items:center;gap:8px;">
                <span style="background:var(--bg);border:1px solid var(--border);border-radius:20px;padding:2px 9px;font-size:11px;font-weight:500;color:var(--text-muted);">No card fee</span>
                <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="var(--text-muted)" stroke-width="2" :style="expanded.airbnb ? 'transform:rotate(180deg);' : ''"><polyline points="4,6 8,10 12,6"/></svg>
              </div>
            </div>
            <div v-if="expanded.airbnb" style="padding:14px 16px;border-top:1px solid var(--border);">
              <div style="font-size:12px;color:var(--text-muted);line-height:1.6;background:var(--bg);border-radius:8px;padding:10px 14px;">Airbnb collects payment from guests and pays you directly. Their host fee of 15.51% covers all payment processing. There is no separate card fee to configure for Airbnb bookings.</div>
            </div>
          </div>

          <!-- Booking.com -->
          <div class="payment-platform-row" style="margin-top:8px;">
            <div class="payment-platform-header" @click="expanded.booking = !expanded.booking">
              <div style="display:flex;align-items:center;gap:10px;">
                <div class="ota-logo booking" style="width:30px;height:30px;border-radius:7px;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:600;">BK</div>
                <div>
                  <div style="font-size:13px;font-weight:600;color:var(--text);">Booking.com</div>
                  <div style="font-size:11px;color:var(--text-muted);margin-top:1px;">Choose how guests pay for Booking.com reservations</div>
                </div>
              </div>
              <div style="display:flex;align-items:center;gap:8px;">
                <span :style="badgeStyle(bkPayment.badge.style) + 'border-radius:20px;padding:2px 9px;font-size:11px;font-weight:500;'">{{ bkPayment.badge.text }}</span>
                <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="var(--text-muted)" stroke-width="2" :style="expanded.booking ? 'transform:rotate(180deg);' : ''"><polyline points="4,6 8,10 12,6"/></svg>
              </div>
            </div>
            <div v-if="expanded.booking" style="padding:14px 16px;border-top:1px solid var(--border);">
              <div style="font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:0.07em;color:var(--text-muted);margin-bottom:10px;">How do guests pay for Booking.com reservations?</div>
              <div style="display:flex;flex-direction:column;gap:7px;margin-bottom:14px;">
                <div class="payment-radio" :class="{ active: bkPayment.method === 'vcc' }" @click="selectBkPayment('vcc')">
                  <div class="payment-radio-dot" :class="{ active: bkPayment.method === 'vcc' }"></div>
                  <div><div style="font-size:13px;font-weight:600;color:var(--text);">Payments by Booking.com (VCC)</div><div style="font-size:11px;color:var(--text-muted);margin-top:2px;">Booking.com collect payment and charge you 1.6%. Fee pulled from booking data where available.</div></div>
                </div>
                <div class="payment-radio" :class="{ active: bkPayment.method === 'bank' }" @click="selectBkPayment('bank')">
                  <div class="payment-radio-dot" :class="{ active: bkPayment.method === 'bank' }"></div>
                  <div><div style="font-size:13px;font-weight:600;color:var(--text);">Bank transfer</div><div style="font-size:11px;color:var(--text-muted);margin-top:2px;">You collect payment directly via bank transfer. No card processing fee applies.</div></div>
                </div>
                <div class="payment-radio" :class="{ active: bkPayment.method === 'stripe' }" @click="selectBkPayment('stripe')">
                  <div class="payment-radio-dot" :class="{ active: bkPayment.method === 'stripe' }"></div>
                  <div><div style="font-size:13px;font-weight:600;color:var(--text);">Stripe</div><div style="font-size:11px;color:var(--text-muted);margin-top:2px;">You process payment via Stripe. Fee calculated per transaction based on card type.</div></div>
                </div>
                <div class="payment-radio" :class="{ active: bkPayment.method === 'custom' }" @click="selectBkPayment('custom')">
                  <div class="payment-radio-dot" :class="{ active: bkPayment.method === 'custom' }"></div>
                  <div><div style="font-size:13px;font-weight:600;color:var(--text);">Custom fee</div><div style="font-size:11px;color:var(--text-muted);margin-top:2px;">Set your own processing or admin fee to pass through to the owner.</div></div>
                </div>
              </div>
              <div v-if="bkPayment.method === 'vcc'">
                <div class="field-grid">
                  <div class="field"><label class="field-label">Processing rate</label><div class="input-prefix"><span>%</span><input type="number" value="1.6" step="0.1"/></div><div class="field-hint">Pulled from booking data where available — this is the fallback rate</div></div>
                </div>
              </div>
              <div v-if="bkPayment.method === 'stripe'">
                <div class="field-grid">
                  <div class="field"><label class="field-label">Blended rate</label><div class="input-prefix"><span>%</span><input type="number" value="1.8" step="0.1"/></div><div class="field-hint">Average rate across your typical card mix. UK cards ~1.5%, European ~2.5%, international ~3.25%.</div></div>
                  <div class="field"><label class="field-label">Fixed fee per transaction</label><div class="input-prefix"><span>£</span><input type="number" value="0.30" step="0.01"/></div><div class="field-hint">30p per transaction. Check your Stripe dashboard for exact fees.</div></div>
                </div>
              </div>
              <div v-if="bkPayment.method === 'custom'">
                <div class="field-grid">
                  <div class="field"><label class="field-label">Fee type</label><select><option>Percentage of booking</option><option>Fixed amount per booking</option></select></div>
                  <div class="field"><label class="field-label">Amount</label><div class="input-prefix"><span>%</span><input type="number" value="2.0" step="0.1"/></div></div>
                </div>
              </div>
            </div>
          </div>

          <!-- Direct -->
          <div class="payment-platform-row" style="margin-top:8px;">
            <div class="payment-platform-header" @click="expanded.direct = !expanded.direct">
              <div style="display:flex;align-items:center;gap:10px;">
                <div class="ota-logo custom" style="width:30px;height:30px;border-radius:7px;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:600;">DR</div>
                <div>
                  <div style="font-size:13px;font-weight:600;color:var(--text);">Direct bookings</div>
                  <div style="font-size:11px;color:var(--text-muted);margin-top:1px;">Configure how you take payment for direct reservations</div>
                </div>
              </div>
              <div style="display:flex;align-items:center;gap:8px;">
                <span :style="badgeStyle(drPayment.badge.style) + 'border-radius:20px;padding:2px 9px;font-size:11px;font-weight:500;'">{{ drPayment.badge.text }}</span>
                <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="var(--text-muted)" stroke-width="2" :style="expanded.direct ? 'transform:rotate(180deg);' : ''"><polyline points="4,6 8,10 12,6"/></svg>
              </div>
            </div>
            <div v-if="expanded.direct" style="padding:14px 16px;border-top:1px solid var(--border);">
              <div style="font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:0.07em;color:var(--text-muted);margin-bottom:10px;">How do you take payment for direct bookings?</div>
              <div style="display:flex;flex-direction:column;gap:7px;margin-bottom:14px;">
                <div class="payment-radio" :class="{ active: drPayment.method === 'bank' }" @click="selectDrPayment('bank')">
                  <div class="payment-radio-dot" :class="{ active: drPayment.method === 'bank' }"></div>
                  <div><div style="font-size:13px;font-weight:600;color:var(--text);">Bank transfer</div><div style="font-size:11px;color:var(--text-muted);margin-top:2px;">No processing fee. Owner receives full settlement amount.</div></div>
                </div>
                <div class="payment-radio" :class="{ active: drPayment.method === 'stripe' }" @click="selectDrPayment('stripe')">
                  <div class="payment-radio-dot" :class="{ active: drPayment.method === 'stripe' }"></div>
                  <div><div style="font-size:13px;font-weight:600;color:var(--text);">Stripe</div><div style="font-size:11px;color:var(--text-muted);margin-top:2px;">Estimated rate applied to Stripe transactions. Set a rate that reflects your typical card mix.</div></div>
                </div>
                <div class="payment-radio" :class="{ active: drPayment.method === 'custom' }" @click="selectDrPayment('custom')">
                  <div class="payment-radio-dot" :class="{ active: drPayment.method === 'custom' }"></div>
                  <div><div style="font-size:13px;font-weight:600;color:var(--text);">Custom admin fee</div><div style="font-size:11px;color:var(--text-muted);margin-top:2px;">Charge a flat processing or admin fee to cover payment handling costs.</div></div>
                </div>
                <div class="payment-radio" :class="{ active: drPayment.method === 'mixed' }" @click="selectDrPayment('mixed')">
                  <div class="payment-radio-dot" :class="{ active: drPayment.method === 'mixed' }"></div>
                  <div><div style="font-size:13px;font-weight:600;color:var(--text);">Mixed — varies per booking</div><div style="font-size:11px;color:var(--text-muted);margin-top:2px;">Payment method differs booking by booking. You will set it manually on each report.</div></div>
                </div>
              </div>
              <div v-if="drPayment.method === 'stripe'">
                <div class="field-grid">
                  <div class="field"><label class="field-label">Blended rate</label><div class="input-prefix"><span>%</span><input type="number" value="1.8" step="0.1"/></div><div class="field-hint">Average rate across your typical card mix. UK cards ~1.5%, European ~2.5%, international ~3.25%.</div></div>
                  <div class="field"><label class="field-label">Fixed fee per transaction</label><div class="input-prefix"><span>£</span><input type="number" value="0.30" step="0.01"/></div><div class="field-hint">30p per transaction. Check your Stripe dashboard for exact fees.</div></div>
                </div>
              </div>
              <div v-if="drPayment.method === 'custom'">
                <div class="field-grid">
                  <div class="field"><label class="field-label">Fee type</label><select><option>Percentage of booking</option><option>Fixed amount per booking</option></select></div>
                  <div class="field"><label class="field-label">Amount</label><div class="input-prefix"><span>%</span><input type="number" value="2.0" step="0.1"/></div></div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>

    <div class="btn-row-actions" style="margin-top:4px;">
      <button class="btn btn-outline">Reset to saved</button>
      <button class="btn btn-primary">Save all platform settings</button>
    </div>

  </div>
</template>

<script>
const BK_OPTIONS = {
  vcc:    { text: 'Payments by Booking.com · 1.6%', style: 'green' },
  bank:   { text: 'Bank transfer · no fee',          style: 'neutral' },
  stripe: { text: 'Stripe payment',                  style: 'amber' },
  custom: { text: 'Custom fee',                      style: 'amber' }
};

const DR_OPTIONS = {
  bank:   { text: 'Bank transfer · no fee',     style: 'neutral' },
  stripe: { text: 'Stripe payment',             style: 'amber' },
  custom: { text: 'Custom admin fee',           style: 'amber' },
  mixed:  { text: 'Mixed — varies per booking', style: 'amber' }
};

export default {
  name: 'PlatformsTab',
  data() {
    return {
      feeSource: 'channel',
      rates: {
        airbnb: 15.51,
        booking: 15.00,
        vrbo: 8.00,
        direct: 10.00,
        other: 10.00
      },
      expanded: {
        airbnb: false,
        booking: false,
        direct: false
      },
      bkPayment: {
        method: 'vcc',
        badge: BK_OPTIONS.vcc
      },
      drPayment: {
        method: 'mixed',
        badge: DR_OPTIONS.mixed
      }
    };
  },
  methods: {
    selectBkPayment(method) {
      this.bkPayment.method = method;
      this.bkPayment.badge = BK_OPTIONS[method];
    },
    selectDrPayment(method) {
      this.drPayment.method = method;
      this.drPayment.badge = DR_OPTIONS[method];
    },
    badgeStyle(style) {
      if (style === 'green') {
        return 'background:var(--green-dim);border:1px solid var(--green-border);color:var(--green);';
      }
      if (style === 'amber') {
        return 'background:var(--amber-dim);border:1px solid var(--amber-border);color:var(--amber-text);';
      }
      return 'background:var(--bg);border:1px solid var(--border);color:var(--text-muted);';
    }
  }
};
</script>

<style scoped>
.settings-design {
  --accent: #5b4fcf;
  --accent-dim: rgba(91,79,207,0.08);
  --accent-border: rgba(91,79,207,0.25);
  --bg: #f5f4fb;
  --white: #ffffff;
  --text: #1a1a2e;
  --text-mid: #4a4880;
  --text-muted: #9896bb;
  --border: #e8e6f4;
  --green: #00b894;
  --green-dim: rgba(0,184,148,0.1);
  --green-border: rgba(0,184,148,0.25);
  --amber: #fdcb6e;
  --amber-text: #b7791f;
  --amber-dim: rgba(253,203,110,0.2);
  --amber-border: rgba(253,203,110,0.5);
  --shadow-sm: 0 1px 4px rgba(26,24,61,0.07);
  --radius: 10px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: var(--text);
  -webkit-font-smoothing: antialiased;
}
.settings-design *, .settings-design *::before, .settings-design *::after { box-sizing: border-box; }

/* INFO BOX */
.info-box { display: flex; align-items: flex-start; gap: 10px; background: var(--accent-dim); border: 1px solid var(--accent-border); border-radius: 8px; padding: 11px 14px; font-size: 12px; color: var(--text-mid); line-height: 1.6; margin-bottom: 16px; }
.info-box svg { width: 14px; height: 14px; fill: none; stroke: var(--accent); stroke-width: 2; flex-shrink: 0; margin-top: 1px; }

/* CARDS */
.card { background: var(--white); border-radius: var(--radius); border: 1px solid var(--border); box-shadow: var(--shadow-sm); overflow: hidden; margin-bottom: 18px; }
.card-header { padding: 16px 22px; border-bottom: 1px solid var(--border); display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
.card-title { font-family: 'Outfit', sans-serif; font-size: 0.9rem; font-weight: 700; color: var(--text); display: flex; align-items: center; gap: 8px; }
.card-title svg { width: 15px; height: 15px; fill: none; stroke: var(--accent); stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
.card-subtitle { font-size: 12px; color: var(--text-muted); margin-top: 2px; }
.card-body { padding: 20px 22px; }

/* FIELDS */
.field-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.field { display: flex; flex-direction: column; gap: 5px; }
label.field-label { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.07em; color: var(--text-muted); }
input[type=text], input[type=number], select {
  border: 1.5px solid var(--border); border-radius: 8px; padding: 9px 12px; font-size: 13px; color: var(--text);
  font-family: 'Inter', sans-serif; background: var(--white); outline: none;
  transition: border-color 0.15s, box-shadow 0.15s; width: 100%;
}
input:focus, select:focus { border-color: var(--accent); box-shadow: 0 0 0 3px rgba(91,79,207,0.1); }
select { -webkit-appearance: none; appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%239896bb'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 12px center; background-size: 8px; padding-right: 30px; }
.field-hint { font-size: 11px; color: var(--text-muted); line-height: 1.5; margin-top: 1px; }
.input-prefix { display: flex; align-items: center; }
.input-prefix span { background: var(--bg); border: 1.5px solid var(--border); border-right: none; border-radius: 8px 0 0 8px; padding: 9px 10px; font-size: 13px; color: var(--text-muted); white-space: nowrap; flex-shrink: 0; }
.input-prefix input { border-radius: 0 8px 8px 0; }

/* BUTTONS */
.btn { display: inline-flex; align-items: center; gap: 6px; padding: 8px 16px; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer; border: none; font-family: 'Inter', sans-serif; transition: opacity 0.15s, transform 0.1s; white-space: nowrap; }
.btn:hover { opacity: 0.88; }
.btn:active { transform: scale(0.98); }
.btn-primary { background: var(--accent); color: #fff; }
.btn-outline { background: transparent; color: var(--text-mid); border: 1.5px solid var(--border); }
.btn-sm { padding: 5px 11px; font-size: 12px; }
.btn svg { width: 13px; height: 13px; fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round; }
.btn-row-actions { display: flex; gap: 8px; justify-content: flex-end; margin-top: 20px; padding-top: 16px; border-top: 1px solid var(--border); }

/* RADIO */
.radio-opt { display: flex; align-items: flex-start; gap: 10px; padding: 11px 14px; border: 1.5px solid var(--border); border-radius: 9px; cursor: pointer; transition: border-color 0.15s, background 0.15s; flex: 1; }
.radio-opt:hover { border-color: var(--accent); background: var(--accent-dim); }
.radio-opt.active { border-color: var(--accent); background: var(--accent-dim); }
.radio-dot-wrap { width: 16px; height: 16px; border-radius: 50%; border: 2px solid var(--border); flex-shrink: 0; display: flex; align-items: center; justify-content: center; margin-top: 1px; }
.radio-opt.active .radio-dot-wrap { border-color: var(--accent); background: var(--accent); }
.radio-opt.active .radio-dot-wrap::after { content: ''; width: 6px; height: 6px; border-radius: 50%; background: #fff; }

/* OTA TABLE */
.ota-table { width: 100%; border-collapse: collapse; }
.ota-table thead th { padding: 8px 12px; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.07em; color: var(--text-muted); border-bottom: 1px solid var(--border); text-align: left; background: var(--bg); }
.ota-table tbody tr { border-bottom: 1px solid #f0eef8; transition: background 0.1s; }
.ota-table tbody tr:last-child { border-bottom: none; }
.ota-table td { padding: 11px 12px; font-size: 13px; vertical-align: middle; }
.ota-name { font-weight: 600; color: var(--text); display: flex; align-items: center; gap: 8px; }
.ota-logo { width: 28px; height: 28px; border-radius: 7px; display: flex; align-items: center; justify-content: center; font-family: 'Outfit', sans-serif; font-size: 10px; font-weight: 800; flex-shrink: 0; }
.ota-logo.airbnb { background: #FFE4DE; color: #FF5A5F; }
.ota-logo.booking { background: #DEEEFF; color: #003580; }
.ota-logo.vrbo { background: #E8F4E8; color: #1A6E20; }
.ota-logo.custom { background: var(--accent-dim); color: var(--accent); }
.ota-hint { font-size: 11px; color: var(--text-muted); margin-top: 2px; margin-left: 36px; }
.ota-rate-input { width: 72px; text-align: right; padding: 5px 8px !important; }
.ota-effective { font-size: 13px; font-weight: 700; color: var(--text); min-width: 52px; text-align: right; display: block; }
.vat-tag { font-size: 10px; padding: 2px 7px; border-radius: 20px; white-space: nowrap; font-weight: 600; }
.vat-on-top { background: var(--amber-dim); color: var(--amber-text); border: 1px solid var(--amber-border); }
.vat-inc { background: var(--green-dim); color: #007a63; border: 1px solid var(--green-border); }
.vat-unknown { background: var(--bg); color: var(--text-muted); border: 1px solid var(--border); }

/* PAYMENT PLATFORM */
.payment-platform-row { border: 1px solid var(--border); border-radius: var(--radius); overflow: hidden; }
.payment-platform-header { padding: 12px 16px; display: flex; align-items: center; justify-content: space-between; background: var(--white); cursor: pointer; transition: background 0.15s; }
.payment-platform-header:hover { background: var(--bg); }
.payment-radio { display: flex; align-items: flex-start; gap: 10px; padding: 10px 14px; border-radius: 8px; border: 1.5px solid var(--border); cursor: pointer; transition: all 0.15s; }
.payment-radio:hover { border-color: var(--accent-border); background: var(--accent-dim); }
.payment-radio.active { border-color: var(--accent); background: var(--accent-dim); }
.payment-radio-dot { width: 15px; height: 15px; border-radius: 50%; border: 2px solid var(--border); flex-shrink: 0; margin-top: 2px; transition: all 0.15s; }
.payment-radio-dot.active { border-color: var(--accent); background: var(--accent); box-shadow: inset 0 0 0 3px white; }

@media (max-width: 768px) {
  .card { margin-bottom: 14px; }
  .card-header { padding: 14px 16px; flex-wrap: wrap; gap: 8px; }
  .card-body { padding: 16px 16px; }
  .card-title { font-size: 0.88rem; }
  .card-subtitle { font-size: 11.5px; }
  .field-grid { grid-template-columns: 1fr !important; gap: 12px; }
  .field.span2 { grid-column: 1; }
  .payment-platform-header { padding: 10px 12px; flex-wrap: wrap; gap: 8px; }
  .payment-radio { padding: 8px 12px; }
}
</style>
